// ============================================================================
// ENGAGEMENT CLOSE MODAL
// ============================================================================
// Two-section modal:
//   A — Pre-flight summary  (chargeable totals at all 3 rates, disbursement,
//       budget, prior-closure prefill).
//   B — Closure inputs      (closing date, invoiced amount, invoice ref,
//       rate basis radio, remarks).
//
// If the engagement has unsubmitted DRAFT timesheet entries, the modal
// renders a BLOCK view listing those entries — the user must clear them
// before closure is allowed.
//
// On confirm: ConfirmDialog → POST /Engagements/{id}/close.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useClosurePreview, useCloseEngagement } from 'hooks/api/useEngagement';
import type {
  Engagement,
  CloseEngagementRequest,
  EngagementRateBasis,
} from 'api/types';

interface SavedCloseState {
  closingDate: string;
  invoicedAmount: string;
  invoiceReference: string;
  rateBasis: EngagementRateBasis;
  remarks: string;
}

interface EngagementCloseModalProps {
  isOpen: boolean;
  engagement: Engagement | null;
  onClose: () => void;
  /** Called after a successful close so the parent screen can refresh its list. */
  onClosed?: (updated: Engagement) => void;
  // ---- Minimize-to-bar support ----
  tabId?: string;
  moduleName?: string;
  /** Called when the user restores a previously minimized close dialog. */
  onRestoreModal?: (engagement: Engagement, savedState?: SavedCloseState) => void;
  /** Initial state injected by the parent when reopening a restored session. */
  restoredState?: SavedCloseState | null;
}

function formatCurrency(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `₹ ${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatHours(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${Number(n).toFixed(2)} h`;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const RATE_BASIS_LABEL: Record<EngagementRateBasis, string> = {
  STANDARD: 'Standard',
  DISCOUNTED: 'Discounted',
  PREMIUM: 'Premium',
};

const EngagementCloseModal: React.FC<EngagementCloseModalProps> = ({
  isOpen,
  engagement,
  onClose,
  onClosed,
  tabId,
  moduleName,
  onRestoreModal,
  restoredState,
}) => {
  const { data: preview, loading: previewLoading } = useClosurePreview(
    engagement?.id || null,
    isOpen,
  );
  const { closeEngagement, loading: saving } = useCloseEngagement();
  const { minimize } = useModalMinimize();

  // Local form state
  const [closingDate, setClosingDate] = useState(todayIso());
  const [invoicedAmount, setInvoicedAmount] = useState<string>('');
  const [invoiceReference, setInvoiceReference] = useState('');
  const [rateBasis, setRateBasis] = useState<EngagementRateBasis>('DISCOUNTED');
  const [remarks, setRemarks] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Refs so handleMinimize captures the latest values without re-binding.
  const stateRef = useRef<SavedCloseState>({
    closingDate, invoicedAmount, invoiceReference, rateBasis, remarks,
  });
  stateRef.current = { closingDate, invoicedAmount, invoiceReference, rateBasis, remarks };
  const engagementRef = useRef(engagement);
  engagementRef.current = engagement;

  // Reset / prefill on (re)open
  useEffect(() => {
    if (!isOpen) return;
    setFieldErrors({});
    // 1) Restored from a minimized session — re-hydrate the user's prior inputs verbatim.
    if (restoredState) {
      setClosingDate(restoredState.closingDate || todayIso());
      setInvoicedAmount(restoredState.invoicedAmount || '');
      setInvoiceReference(restoredState.invoiceReference || '');
      setRateBasis(restoredState.rateBasis || 'DISCOUNTED');
      setRemarks(restoredState.remarks || '');
      return;
    }
    // 2) Pre-fill from the most recent reversed closure (re-close after reopen).
    if (preview?.priorClosure) {
      setClosingDate(preview.priorClosure.closingDate?.split('T')[0] || todayIso());
      setInvoicedAmount(String(preview.priorClosure.invoicedAmount ?? ''));
      setInvoiceReference(preview.priorClosure.invoiceReference || '');
      setRateBasis(preview.priorClosure.rateBasis);
      setRemarks(preview.priorClosure.remarks || '');
    } else {
      // 3) Fresh session.
      setClosingDate(todayIso());
      setInvoicedAmount('');
      setInvoiceReference('');
      setRateBasis('DISCOUNTED');
      setRemarks('');
    }
  }, [isOpen, preview?.priorClosure, restoredState]);

  // Minimize: park the modal in the bar and pass the captured state back to the
  // parent on restore so the user does not lose their entered values.
  const handleMinimize = useCallback(() => {
    if (!engagementRef.current || !tabId) return;
    const savedEngagement = engagementRef.current;
    const savedState = { ...stateRef.current };
    const id = `${tabId}-engagement-close-${savedEngagement.code}`;
    const title = `Close - ${savedEngagement.code}`;
    minimize({
      id,
      title,
      moduleName: moduleName || 'Engagement',
      tabId,
      isDirty:
        !!savedState.invoicedAmount.trim() ||
        !!savedState.invoiceReference.trim() ||
        !!savedState.remarks.trim(),
      onRestore: () => {
        if (onRestoreModal) onRestoreModal(savedEngagement, savedState);
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onRestoreModal, onClose]);

  const loggedAtBasis = useMemo(() => {
    if (!preview) return 0;
    if (rateBasis === 'STANDARD') return preview.loggedAmountStandard;
    if (rateBasis === 'PREMIUM')  return preview.loggedAmountPremium;
    return preview.loggedAmountDiscounted;
  }, [preview, rateBasis]);

  const parsedInvoiced = useMemo(() => {
    const v = parseFloat(invoicedAmount);
    return Number.isFinite(v) && v >= 0 ? v : 0;
  }, [invoicedAmount]);

  const variance = useMemo(() => parsedInvoiced - loggedAtBasis, [parsedInvoiced, loggedAtBasis]);

  const handleAttemptClose = () => {
    const errors: Record<string, string> = {};
    if (!closingDate) errors.closingDate = 'Closing date is required';
    if (!invoicedAmount.trim() || !Number.isFinite(parseFloat(invoicedAmount)) || parseFloat(invoicedAmount) < 0) {
      errors.invoicedAmount = 'Enter a non-negative amount';
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setConfirmOpen(true);
  };

  const handleConfirmedClose = async () => {
    if (!engagement) return;
    const request: CloseEngagementRequest = {
      closingDate,
      invoicedAmount: parsedInvoiced,
      invoiceReference: invoiceReference.trim() || null,
      rateBasis,
      remarks: remarks.trim() || null,
      rowVersion: engagement.rowVersion,
    };
    try {
      const updated = await closeEngagement(engagement.id, request);
      setConfirmOpen(false);
      onClosed?.(updated);
      onClose();
      notificationService.toast.success(`Engagement ${engagement.code} closed.`);
    } catch (err: any) {
      setConfirmOpen(false);
      const apiMsg =
        err?.response?.data?.message ||
        err?.message ||
        'Unable to close the engagement. Please try again.';
      notificationService.toast.error(apiMsg, 6000);
    }
  };

  if (!isOpen || !engagement) return null;

  const blocked = preview ? !preview.canClose : false;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div
        className="modal-container modal-lg"
        style={{ maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}
      >
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            Close Engagement &mdash; {engagement.code}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={saving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose} disabled={saving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          {/* Engagement banner */}
          <div
            style={{
              padding: '0.6rem 0.8rem',
              background: 'var(--bg-secondary, rgba(99,102,241,0.06))',
              borderRadius: 6,
              marginBottom: '1rem',
              fontSize: '0.85rem',
              color: 'var(--text-secondary)',
            }}
          >
            <strong>{engagement.clientName}</strong>
            {' — '}
            {engagement.engagementTypeName}
            {' — '}
            {engagement.year}
            {preview?.priorClosure && (
              <span style={{ marginLeft: '0.6rem', color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                (prior closure prefilled)
              </span>
            )}
          </div>

          {previewLoading && (
            <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              Loading engagement totals...
            </div>
          )}

          {/* ============================================================ */}
          {/*  BLOCKED VIEW  — unsubmitted DRAFT entries                    */}
          {/* ============================================================ */}
          {!previewLoading && preview && blocked && (
            <div>
              <div
                style={{
                  padding: '0.75rem 1rem',
                  border: '1px solid #f59e0b',
                  background: 'rgba(245,158,11,0.1)',
                  borderRadius: 6,
                  marginBottom: '0.75rem',
                  fontSize: '0.85rem',
                  color: '#b45309',
                }}
              >
                <strong>Cannot close.</strong> {preview.unsubmittedEntries.length} unsubmitted
                timesheet entr{preview.unsubmittedEntries.length === 1 ? 'y' : 'ies'} on
                this engagement must be submitted first.
              </div>
              <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--grid-header-bg, #dbeafe)', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left' }}>Employee</th>
                      <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left' }}>Week Start</th>
                      <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left' }}>Work Date</th>
                      <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left' }}>Activity</th>
                      <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.unsubmittedEntries.map((u) => (
                      <tr key={u.entryId} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '0.35rem 0.5rem' }}>
                          {u.employeeName || u.employeeCode || u.employeeId}
                        </td>
                        <td style={{ padding: '0.35rem 0.5rem' }}>{u.weekStartDate?.split('T')[0]}</td>
                        <td style={{ padding: '0.35rem 0.5rem' }}>{u.workDate?.split('T')[0]}</td>
                        <td style={{ padding: '0.35rem 0.5rem' }}>{u.activityName || '—'}</td>
                        <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>
                          {(u.durationMinutes / 60).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ============================================================ */}
          {/*  NORMAL FLOW  — preview totals + closure inputs               */}
          {/* ============================================================ */}
          {!previewLoading && preview && !blocked && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* SECTION A — Summary */}
              <div>
                <h3 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Activity Summary
                </h3>
                <div
                  style={{
                    border: '1px solid var(--border-color)',
                    borderRadius: 6,
                    padding: '0.6rem 0.8rem',
                    fontSize: '0.85rem',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    rowGap: '0.35rem',
                    columnGap: '1.5rem',
                  }}
                >
                  <span>Chargeable Hours</span>
                  <span style={{ textAlign: 'right' }}>{formatHours(preview.chargeableHours)}</span>

                  <span>Logged @ Standard</span>
                  <span style={{ textAlign: 'right', fontWeight: 600 }}>
                    {formatCurrency(preview.loggedAmountStandard)}
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                      (locks to WIP register)
                    </span>
                  </span>

                  <span>Logged @ Discounted</span>
                  <span style={{ textAlign: 'right' }}>{formatCurrency(preview.loggedAmountDiscounted)}</span>

                  <span>Logged @ Premium</span>
                  <span style={{ textAlign: 'right' }}>{formatCurrency(preview.loggedAmountPremium)}</span>

                  <span>Disbursement Activity</span>
                  <span style={{ textAlign: 'right' }}>{formatCurrency(preview.disbursementTotal)}</span>

                  <span>Budgeted Amount</span>
                  <span style={{ textAlign: 'right' }}>{formatCurrency(preview.budgetAmount)}</span>
                </div>
              </div>

              {/* SECTION B — Closure inputs */}
              <div>
                <h3 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Closure Inputs
                </h3>
                <div className="form-grid-2">
                  <div className="form-group">
                    <label className="form-label">
                      Closing Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className={`form-input${fieldErrors.closingDate ? ' form-field-error' : ''}`}
                      value={closingDate}
                      onChange={(e) => setClosingDate(e.target.value)}
                      disabled={saving}
                    />
                    {fieldErrors.closingDate && (
                      <div className="form-error-message">{fieldErrors.closingDate}</div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      Invoiced Amount <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={`form-input${fieldErrors.invoicedAmount ? ' form-field-error' : ''}`}
                      value={invoicedAmount}
                      onChange={(e) => setInvoicedAmount(e.target.value)}
                      disabled={saving}
                      placeholder="0.00"
                    />
                    {fieldErrors.invoicedAmount && (
                      <div className="form-error-message">{fieldErrors.invoicedAmount}</div>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Invoice Reference</label>
                  <input
                    type="text"
                    className="form-input"
                    value={invoiceReference}
                    onChange={(e) => setInvoiceReference(e.target.value)}
                    disabled={saving}
                    placeholder="Optional invoice number / note"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Closure Basis <span className="text-danger">*</span>
                  </label>
                  <div style={{ display: 'flex', gap: '1.25rem', fontSize: '0.85rem' }}>
                    {(['STANDARD', 'DISCOUNTED', 'PREMIUM'] as EngagementRateBasis[]).map((b) => (
                      <label
                        key={b}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.4rem',
                          cursor: saving ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <input
                          type="radio"
                          name="rate-basis"
                          checked={rateBasis === b}
                          onChange={() => setRateBasis(b)}
                          disabled={saving}
                        />
                        {RATE_BASIS_LABEL[b]}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Remarks</label>
                  <textarea
                    className="form-input"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    disabled={saving}
                    rows={2}
                    style={{ resize: 'vertical' }}
                  />
                </div>

                {/* Live variance preview */}
                <div
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.6rem 0.8rem',
                    border: `1px solid ${variance < 0 ? '#dc2626' : '#16a34a'}`,
                    background:
                      variance < 0
                        ? 'rgba(220,38,38,0.06)'
                        : variance > 0
                        ? 'rgba(22,163,74,0.06)'
                        : 'var(--bg-secondary, rgba(99,102,241,0.04))',
                    borderRadius: 6,
                    fontSize: '0.85rem',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    rowGap: '0.3rem',
                  }}
                >
                  <span>Logged Value (at basis)</span>
                  <span style={{ textAlign: 'right' }}>{formatCurrency(loggedAtBasis)}</span>
                  <span>Invoiced Amount</span>
                  <span style={{ textAlign: 'right' }}>{formatCurrency(parsedInvoiced)}</span>
                  <span style={{ fontWeight: 600 }}>
                    {variance >= 0 ? 'WIP Profit' : 'WIP Write-off'}
                  </span>
                  <span
                    style={{
                      textAlign: 'right',
                      fontWeight: 600,
                      color: variance < 0 ? '#dc2626' : variance > 0 ? '#16a34a' : 'inherit',
                    }}
                  >
                    {formatCurrency(Math.abs(variance))}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAttemptClose}
            disabled={saving || previewLoading || blocked || !preview}
          >
            <IconSystem.lock size={16} />{' '}
            {blocked
              ? `Cannot close (${preview?.unsubmittedEntries.length ?? 0} unsubmitted)`
              : 'Close Engagement'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title={variance < 0 ? 'Confirm Close at Write-off' : 'Confirm Close'}
        message={
          variance < 0
            ? `Close engagement ${engagement.code} at a write-off of ${formatCurrency(Math.abs(variance))}?`
            : variance > 0
              ? `Close engagement ${engagement.code} at a profit of ${formatCurrency(variance)}?`
              : `Close engagement ${engagement.code} (no variance)?`
        }
        confirmLabel="Close Engagement"
        cancelLabel="Back"
        variant={variance < 0 ? 'danger' : 'default'}
        loading={saving}
        loadingMessage="Closing engagement..."
        onConfirm={handleConfirmedClose}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default EngagementCloseModal;
